/**
 * What an image may be before it is decoded (decision 56; part 16a log, table L), the size it is kept at (decision 37,
 * table F) and the format it is stored in (tables C and T).
 */
import { readImageHeader } from './header.ts';
import type { HeaderProblem, ImageFormat, ImageHeader } from './header.ts';

/** L1: the most bytes an image file may have (decision 56's 50 MB input limit). */
export const MAX_IMAGE_BYTES = 50 * 1024 * 1024;

/** L3: the longest side an image may have to be decoded, in pixels. */
export const MAX_IMAGE_SIDE = 16_384;

/** L4: the most pixels an image may have to be decoded (about 200 MB once decoded). */
export const MAX_IMAGE_PIXELS = 50_000_000;

/** Why an image isn't taken: table H's problems, more bytes than L1 allows, or more pixels than L3 or L4 allow. */
export type ImageRefusal = HeaderProblem | 'too-large' | 'too-big';

/** Table L: the header of an image that may be decoded, or why it may not; each check before the next reads anything. */
export function checkImageInput(bytes: Uint8Array): ImageHeader | ImageRefusal {
  if (bytes.length > MAX_IMAGE_BYTES) return 'too-large';
  const header = readImageHeader(bytes);
  if (typeof header === 'string') return header;
  const { width, height } = header;
  if (width > MAX_IMAGE_SIDE || height > MAX_IMAGE_SIDE || width * height > MAX_IMAGE_PIXELS) return 'too-big';
  return header;
}

/** What an image is for: the text (kept at about 1600 px) or the cover (2560 px). */
export type ImagePurpose = 'text' | 'cover';

/** Table F: the longest side kept for each purpose (decision 37). */
export const KEPT_SIDE: Readonly<Record<ImagePurpose, number>> = Object.freeze({ text: 1600, cover: 2560 });

/** A size in pixels. */
export interface PixelSize {
  readonly width: number;
  readonly height: number;
}

/** Table F: the size an image of `width` × `height` is kept at: as it is (F1), or scaled down to the purpose's side (F2). */
export function fitImage(width: number, height: number, purpose: ImagePurpose): PixelSize {
  const side = KEPT_SIDE[purpose];
  // F1: a scale of 1 keeps the size as it is.
  const scale = Math.min(1, side / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/**
 * C2: the most bytes a pixel a PNG may cost and still be kept: lossless where it is cheap (the user's choice after
 * deep-fix r3). Measured at 1600 px in Chromium, Firefox and WebKit: coloured line art scaled down 0.12–0.19, photos
 * (colour or grey) 1.0–1.3. A PNG's size hardly differs between engines; a JPEG's does, so it is no yardstick.
 */
export const LOSSLESS_BYTES_PER_PIXEL = 0.5;

/**
 * T2: the least alpha a drawn pixel may have and the picture still count as opaque. A browser's scaling can leave a pixel
 * of an opaque picture short of 255 (WebKit's leaves 254 on some); a translucency of 2 % doesn't show.
 */
export const OPAQUE_ALPHA = 250;

/**
 * What pixels are stored as: a PNG (C1), or `either` when the PNG is kept if {@link keepFormat} says it is cheap and the
 * JPEG otherwise (C2); and whether the picture is opaque (and so is flattened before encoding, T4).
 */
export interface FormatChoice {
  readonly format: 'png' | 'either';
  readonly opaque: boolean;
}

/**
 * Tables C and T: how to store pixels (RGBA, four bytes a pixel). The picture is transparent only when the file can hold
 * transparency (`alpha`, the header's; T1) and some pixel's alpha is under {@link OPAQUE_ALPHA} (T2, T3): a transparent
 * picture is a PNG (C1); an opaque one is a PNG where {@link keepFormat} finds it cheap, else a JPEG (C2: the user's
 * choice after deep-fix r3, no colour count, since a colour count can't tell line art from a photo).
 */
export function chooseFormat(rgba: Uint8Array | Uint8ClampedArray, alpha: boolean): FormatChoice {
  if (alpha) {
    const view = new DataView(rgba.buffer, rgba.byteOffset, rgba.byteLength);
    for (let at = 3; at < view.byteLength; at += 4) if (view.getUint8(at) < OPAQUE_ALPHA) return { format: 'png', opaque: false };
  }
  return { format: 'either', opaque: true };
}

/** C2: the format an opaque picture of `pixels` is kept in, its PNG `pngBytes` long: the PNG when it is cheap, else the JPEG. */
export function keepFormat(pngBytes: number, pixels: number): ImageFormat {
  return pngBytes <= LOSSLESS_BYTES_PER_PIXEL * pixels ? 'png' : 'jpeg';
}
